import { useOrganizationLimits } from '~publish/contexts/AccountContext'

import { useMyPostTemplates } from './useMyPostTemplates'

export type CanCreatePostTemplate = {
  /**
   * True iff the user is below their plan's post-template limit.
   *
   * KNOWN DIVERGENCE FROM SERVER: The server counts `templates where
   * accountId === me AND organizationId === current`, regardless of
   * visibility. We can't match that on the client today because the
   * GraphQL surface exposes neither a per-user count field nor an author
   * ID on `PostTemplate`; the only "mine" signal we can derive is
   * `owner === 'mine'` (private visibility, server-pinned to the actor).
   * Internal-visibility templates the user authored are therefore
   * undercounted — a free user who created an internal template will
   * pass this gate and hit `LimitReachedError` at the mutation layer.
   *
   * The server enforcement is the source of truth. Closing this gap
   * requires a schema change (either an authoritative
   * `OrganizationLimits.postTemplatesUsed` field or `creatorId` on
   * `PostTemplate`).
   */
  canCreate: boolean
  /**
   * MP (multi-product) customers have a literal limit of 0 — post templates
   * aren't part of their plan. Surface this as a distinct state so the UI can
   * show a "locked" CTA instead of an upgrade prompt.
   */
  isMpCustomer: boolean
  /**
   * True until organization limits have resolved. Callers must gate on this
   * before reading `canCreate`/`isMpCustomer` — org limits load via a
   * separate query from the template list, so treating an unresolved limit
   * as a real 0 would misclassify any customer as MP while their limits are
   * still in flight.
   */
  isLoading: boolean
}

export const useCanCreatePostTemplate = (): CanCreatePostTemplate => {
  const limits = useOrganizationLimits()
  const { templates } = useMyPostTemplates()

  const isLoading = limits === undefined
  const limit = limits?.postTemplates ?? 0
  const existingCount = templates.filter((t) => t.owner === 'mine').length
  const isMpCustomer = !isLoading && limit === 0
  const canCreate = !isLoading && existingCount < limit

  return {
    canCreate,
    isMpCustomer,
    isLoading,
  }
}
